import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Idea } from '../../types';
import FileUpload from '../Attachments/FileUpload';
import AttachmentList from '../Attachments/AttachmentList';

interface IdeaFormProps {
  onClose: () => void;
  isOpen: boolean;
  editingIdea?: Idea | null;
}

const IdeaForm: React.FC<IdeaFormProps> = ({ onClose, isOpen, editingIdea }) => {
  const { state, actions } = useApp();
  const isEditing = !!editingIdea;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Feature' as 'Feature' | 'Customer Request' | 'Product' | 'Process' | 'Technology' | 'Sales and Marketing' | 'Innovation',
    status: 'New' as 'New' | 'Under Review' | 'Approved' | 'In Progress' | 'Completed',
    owner: state.currentUser,
    tags: '',
    effort: 5,
    value: 5,
    plannedQuarter: undefined as 'Q3 2025' | 'Q4 2025' | 'Q1 2026' | 'Q2 2026' | 'Q3 2026' | 'Q4 2026' | 'Q1 2027' | 'Q2 2027' | 'Q3 2027' | undefined,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachmentKey, setAttachmentKey] = useState(0); // Force re-render attachments

  // Initialize form only when modal opens or editing idea changes
  useEffect(() => {
    if (!isOpen) return; // Don't reset if modal is closed
    
    if (editingIdea) {
      setFormData({
        title: editingIdea.title,
        description: editingIdea.description,
        category: editingIdea.category,
        status: editingIdea.status,
        owner: editingIdea.owner,
        tags: editingIdea.tags.join(', '),
        effort: editingIdea.effort,
        value: editingIdea.value,
        plannedQuarter: editingIdea.plannedQuarter,
      });
    } else {
      // Only reset form for new idea when modal first opens
      // Check if we have saved draft data
      const savedDraft = sessionStorage.getItem('ideaFormDraft');
      if (savedDraft) {
        try {
          const draftData = JSON.parse(savedDraft);
          setFormData({
            ...draftData,
            owner: state.currentUser || draftData.owner, // Use current user if available
            plannedQuarter: draftData.plannedQuarter || undefined,
          });
        } catch (error) {
          console.error('Error loading draft:', error);
          setFormData({
            title: '',
            description: '',
            category: 'Feature',
            status: 'New',
            owner: state.currentUser,
            tags: '',
            effort: 5,
            value: 5,
            plannedQuarter: undefined,
          });
        }
      } else {
        setFormData({
          title: '',
          description: '',
          category: 'Feature',
          status: 'New',
          owner: state.currentUser,
          tags: '',
          effort: 5,
          value: 5,
          plannedQuarter: undefined,
        });
      }
      setAttachmentKey(Date.now()); // Reset attachments component
    }
    setErrors({});
  }, [editingIdea, isOpen]); // Removed state.currentUser dependency!

  // Auto-save draft to prevent data loss
  useEffect(() => {
    if (!isOpen || editingIdea) return; // Don't save drafts when editing existing ideas
    
    const hasContent = formData.title.trim() || formData.description.trim() || formData.tags.trim();
    if (hasContent) {
      sessionStorage.setItem('ideaFormDraft', JSON.stringify(formData));
    }
  }, [formData, isOpen, editingIdea]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'effort' || name === 'value' ? parseInt(value) : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleOwnerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedUser = state.users.find(user => user.id === e.target.value);
    if (selectedUser) {
      setFormData(prev => ({ ...prev, owner: selectedUser }));
    }
  };

  const handleAttachmentUpload = () => {
    // Refresh attachment list when new files are uploaded
    setAttachmentKey(Date.now());
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (formData.effort < 1 || formData.effort > 10) {
      newErrors.effort = 'Effort must be between 1 and 10';
    }
    
    if (formData.value < 1 || formData.value > 10) {
      newErrors.value = 'Value must be between 1 and 10';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      const ideaData = {
        ...formData,
        tags: tagsArray,
      };

      if (isEditing && editingIdea) {
        // Update existing idea
        const updatedIdea: Idea = {
          ...editingIdea,
          title: formData.title,
          description: formData.description,
          category: formData.category,
          status: formData.status,
          owner: formData.owner || editingIdea.owner,
          tags: tagsArray,
          effort: formData.effort,
          value: formData.value,
          plannedQuarter: formData.plannedQuarter,
          attachments: editingIdea.attachments || [],
          dateUpdated: new Date(),
          score: formData.value / formData.effort,
        };
        await actions.updateIdea(updatedIdea);
        
        // Show success message
        actions.addNotification({
          type: 'status_change',
          title: 'Idea Updated Successfully',
          message: `Your idea "${formData.title}" has been updated.`,
          read: false,
        });
      } else {
        // Create new idea
        await actions.addIdea(ideaData);
        
        // Show success message
        actions.addNotification({
          type: 'status_change',
          title: 'Idea Submitted Successfully',
          message: `Your idea "${formData.title}" has been submitted for review.`,
          read: false,
        });
      }
      
      // Clear draft on successful submission
      if (!isEditing) {
        sessionStorage.removeItem('ideaFormDraft');
      }
      
      onClose();
    } catch (error) {
      console.error('Error submitting idea:', error);
    } finally {
      setIsSubmitting(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Idea' : 'Submit New Idea'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`input-field ${errors.title ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Enter a descriptive title for your idea"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`input-field ${errors.description ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Provide a detailed description of your idea, including the problem it solves and how it would work"
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="Feature">Feature</option>
                <option value="Customer Request">Customer Request</option>
                <option value="Product">Product</option>
                <option value="Process">Process</option>
                <option value="Technology">Technology</option>
                <option value="Sales and Marketing">Sales and Marketing</option>
                <option value="Innovation">Innovation</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="New">New</option>
                <option value="Under Review">Under Review</option>
                <option value="Approved">Approved</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
          </div>

          {/* Planned Quarter - only show for In Progress status */}
          {formData.status === 'In Progress' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Planned Quarter
              </label>
              <select
                name="plannedQuarter"
                value={formData.plannedQuarter || ''}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="">Select a quarter...</option>
                <option value="Q3 2025">Q3 2025</option>
                <option value="Q4 2025">Q4 2025</option>
                <option value="Q1 2026">Q1 2026</option>
                <option value="Q2 2026">Q2 2026</option>
                <option value="Q3 2026">Q3 2026</option>
                <option value="Q4 2026">Q4 2026</option>
                <option value="Q1 2027">Q1 2027</option>
                <option value="Q2 2027">Q2 2027</option>
                <option value="Q3 2027">Q3 2027</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Choose when this idea should appear in the roadmap
              </p>
            </div>
          )}

          {/* Owner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Owner
            </label>
            <select
              value={formData.owner?.id || ''}
              onChange={handleOwnerChange}
              className="input-field"
            >
              {state.users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              className="input-field"
              placeholder="Enter tags separated by commas (e.g., mobile, UI, performance)"
            />
            <p className="mt-1 text-sm text-gray-500">
              Separate multiple tags with commas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Effort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Effort (1-10) *
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  name="effort"
                  min="1"
                  max="10"
                  value={formData.effort}
                  onChange={handleInputChange}
                  className="flex-1"
                />
                <span className="text-lg font-semibold text-energetic-blue w-8 text-center">
                  {formData.effort}
                </span>
              </div>
              {errors.effort && <p className="mt-1 text-sm text-red-600">{errors.effort}</p>}
              <p className="mt-1 text-sm text-gray-500">
                How much effort would this require? (1 = minimal, 10 = significant)
              </p>
            </div>

            {/* Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Value (1-10) *
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  name="value"
                  min="1"
                  max="10"
                  value={formData.value}
                  onChange={handleInputChange}
                  className="flex-1"
                />
                <span className="text-lg font-semibold text-electric-lime w-8 text-center">
                  {formData.value}
                </span>
              </div>
              {errors.value && <p className="mt-1 text-sm text-red-600">{errors.value}</p>}
              <p className="mt-1 text-sm text-gray-500">
                How valuable would this be? (1 = nice to have, 10 = critical)
              </p>
            </div>
          </div>

          {/* Score Preview */}
          <div className="bg-ocean-mist p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Calculated Priority Score:
              </span>
              <span className="text-xl font-bold text-deep-teal">
                {(formData.value / formData.effort).toFixed(2)}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Higher scores indicate better value-to-effort ratio
            </p>
          </div>

          {/* File Attachments */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments
              </label>
              
              {editingIdea?.id ? (
                <FileUpload
                  ideaId={editingIdea.id}
                  onUploadComplete={handleAttachmentUpload}
                  disabled={isSubmitting}
                />
              ) : (
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center bg-gray-50">
                  <p className="text-sm text-gray-500">
                    💡 Attachments can be added after the idea is created
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Create your idea first, then edit it to add files
                  </p>
                </div>
              )}
            </div>
            
            {/* Show existing attachments */}
            {editingIdea?.id && (
              <AttachmentList
                key={attachmentKey}
                ideaId={editingIdea.id}
                onAttachmentDeleted={handleAttachmentUpload}
              />
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Submitting...') 
                : (isEditing ? 'Update Idea' : 'Submit Idea')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IdeaForm; 